import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { logError } from "@/lib/log";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Eye, EyeOff, FileDown, Loader2, Sparkles, ChevronDown, ChevronUp, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const RAG = { green: "bg-emerald-500", amber: "bg-amber-500", red: "bg-red-500" };
const RAG_TEXT = { green: "text-emerald-700", amber: "text-amber-700", red: "text-red-700" };

function Dot({ status, className = "" }) { return <span className={`inline-block w-2.5 h-2.5 rounded-full ${RAG[status]} ${className}`} />; }

function Section({ s, onDeclare, busyId }) {
  const [open, setOpen] = useState(s.status !== "green");
  return (
    <div className="rounded-2xl border border-border bg-card" data-testid={`inspector-section-${s.id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-3 p-4 text-left" data-testid={`inspector-section-toggle-${s.id}`}>
        <Dot status={s.status} className="w-3 h-3" />
        <span className="font-display font-semibold flex-1">{s.label}</span>
        <span className={`text-sm font-medium ${RAG_TEXT[s.status]}`}>{s.green}/{s.total} in place</span>
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>
      {open && (
        <ul className="border-t border-border divide-y divide-border">
          {s.items.map((i) => (
            <li key={i.id} className="flex items-start gap-3 px-4 py-2.5 text-sm" data-testid={`inspector-item-${i.id}`}>
              {i.source === "declared" ? (
                <Checkbox checked={i.status === "green"} disabled={busyId === i.id} onCheckedChange={(v) => onDeclare(i.id, !!v)} className="mt-0.5" data-testid={`inspector-declare-${i.id}`} />
              ) : <Dot status={i.status} className="mt-1.5" />}
              <div className="flex-1 min-w-0">
                <p className={i.status === "green" ? "" : "font-medium"}>{i.label}</p>
                <p className="text-xs text-muted-foreground">{i.detail}{i.source === "declared" && " · self-declared"}</p>
              </div>
              {i.link && i.status !== "green" && <Link to={i.link} className="text-xs text-primary hover:underline whitespace-nowrap inline-flex items-center gap-1">Fix <ArrowRight className="w-3 h-3" /></Link>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function InspectorMode() {
  const [on, setOn] = useState(() => localStorage.getItem("inspector_mode") === "1");
  const [view, setView] = useState(null);
  const [narrative, setNarrative] = useState(null);
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => { localStorage.setItem("inspector_mode", on ? "1" : "0"); if (on && !view) api.get("/me/inspector").then((r) => setView(r.data)).catch((e) => logError("inspector", e)); }, [on, view]);

  const declare = async (item_id, held) => {
    setBusyId(item_id);
    try { const r = await api.put("/me/inspector/declarations", { item_id, held }); setView(r.data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not save"); }
    finally { setBusyId(null); }
  };
  const brief = async () => {
    setBusy(true);
    try { const r = await api.post("/me/inspector/narrative"); setNarrative(r.data.text); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "The assistant is busy"); }
    finally { setBusy(false); }
  };
  const report = async () => {
    const res = await api.get("/me/inspector/report.pdf", { responseType: "blob" });
    const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = "inspection-readiness-report.pdf"; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <section className="rounded-3xl border border-border bg-card/60 p-5 sm:p-6" data-testid="inspector-mode">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px]">
          <p className="font-display text-lg font-bold flex items-center gap-2"><Eye className="w-5 h-5 text-accent" /> Inspector Mode</p>
          <p className="text-sm text-muted-foreground">See your service the way an inspector will — every compliance area, RAG-rated by key question.</p>
        </div>
        <Button variant={on ? "secondary" : "default"} onClick={() => setOn((o) => !o)} data-testid="inspector-mode-toggle">
          {on ? <><EyeOff className="w-4 h-4 mr-1.5" /> Turn off</> : <><Eye className="w-4 h-4 mr-1.5" /> Turn on</>}
        </Button>
      </div>
      {on && !view && <p className="text-sm text-muted-foreground mt-4">Loading…</p>}
      {on && view && (
        <div className="mt-5 space-y-4">
          <div className="flex flex-wrap items-center gap-3 rounded-2xl bg-background border border-border p-4">
            <Dot status={view.overall} className="w-3.5 h-3.5" />
            <p className="font-semibold" data-testid="inspector-overall">{view.ready_pct}% of what an inspector will ask for is in place</p>
            <span className="text-xs text-muted-foreground">{view.framework}</span>
            <span className="flex-1" />
            <Button size="sm" variant="outline" onClick={brief} disabled={busy} data-testid="inspector-narrative-button">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1.5" /> Where will they focus?</>}</Button>
            <Button size="sm" variant="outline" onClick={report} data-testid="inspector-report-button"><FileDown className="w-4 h-4 mr-1.5" /> Readiness report (PDF)</Button>
          </div>
          {narrative && <div className="rounded-2xl border border-accent/30 bg-accent/5 p-4 text-sm leading-relaxed" data-testid="inspector-narrative"><p className="font-semibold mb-1">Where an inspector will focus</p><p>{narrative}</p></div>}
          <div className="grid gap-3">{view.sections.map((s) => <Section key={s.id} s={s} onDeclare={declare} busyId={busyId} />)}</div>
          <p className="text-xs text-muted-foreground">Tick the self-declared items you hold outside the app; they're recorded with a timestamp and marked "self-declared" on the report.</p>
        </div>
      )}
    </section>
  );
}
